import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SiteLogo } from "@/components/Logo";
import { useAuth, GoogleSignInButton } from "@/lib/auth";

const links = [
  { label: "Scoreboards", href: "/#scoreboards" },
  { label: "Odds", href: "/#odds" },
  { label: "Calendar", href: "/calendar" },
  { label: "Teams", href: "/#teams" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(href.slice(2))?.scrollIntoView({ behavior: "smooth" }), 400);
      } else {
        document.getElementById(href.slice(2))?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-bline bg-ink/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <SiteLogo className="h-8 w-8" />
          <span className="font-display text-2xl tracking-wide text-white">
            SPORTSGEAR<span className="text-volt">DFW</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="font-display text-lg tracking-widest text-slate-400 transition-colors hover:text-volt"
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <Link
              to="/account"
              data-testid="nav-account-link"
              className="flex items-center gap-2 rounded-sm border border-bline px-2 py-1 transition-colors hover:border-volt"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-6 w-6 rounded-full object-cover" />
              ) : (
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-elevated font-score text-[10px] text-volt">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </span>
              )}
              <span className="max-w-24 truncate font-score text-xs text-slate-300">{(user.name || user.email).split(" ")[0]}</span>
            </Link>
          ) : (
            <GoogleSignInButton label="Sign in" />
          )}
          <Link
            to="/admin"
            data-testid="nav-link-admin"
            className="rounded-sm border border-bline px-4 py-1.5 font-display text-lg tracking-widest text-slate-300 transition-colors hover:border-volt hover:text-volt"
          >
            ADMIN
          </Link>
        </nav>
        <button data-testid="nav-mobile-toggle" className="text-slate-200 md:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-bline bg-surface px-4 py-4 md:hidden" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <button key={l.label} data-testid={`nav-mobile-${l.label.toLowerCase()}`} onClick={() => go(l.href)} className="block w-full py-2 text-left font-display text-xl tracking-widest text-slate-300 hover:text-volt">
              {l.label}
            </button>
          ))}
          <Link to="/admin" data-testid="nav-mobile-admin" onClick={() => setOpen(false)} className="block py-2 font-display text-xl tracking-widest text-volt">
            ADMIN
          </Link>
        </nav>
      )}
    </header>
  );
}
